import cx from "classnames";
import { DmmHeader } from "components/DmmHeader";
import { Modal } from "components/Modal";
import { graphql, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { isDMM } from "libs/dmm";
import usePlatform from "hooks/usePlatform";
import { useDMMPopupState } from "hooks/useDMMPopupState";
import { useMetadata } from "hooks/useMetadata";
import React, { useState } from "react";

import { Menu, Transition } from "@headlessui/react";
import { useMediaQuery, useToggle } from "@react-hookz/web";
import { useScroll } from "@use-gesture/react";
import { getNewsUrl } from "libs/news";

import type { SocialLink } from "types/metadata";

const LANGUAGES = [
    { display: "繁", language: "zh-tw" },
    { display: "简", language: "zh-cn" },
    { display: "EN", language: "en" },
    { display: "日", language: "ja" },
    { display: "한", language: "ko" },
];

const Logo = React.memo((props: { language: string }) => {
    switch (props.language) {
        case "zh-cn":
            return <StaticImage alt="logo" src="../images/logo-s-zh.png" />;
        case "ja":
            return <StaticImage alt="logo" src="../images/logo-s-ja.png" />;
        case "ko":
            return <StaticImage alt="logo" src="../images/logo-s-ko.png" />;
        case "en":
            return <StaticImage alt="logo" src="../images/logo-s-en.png" />;
        default:
            return <StaticImage alt="logo" src="../images/logo-s-zh.png" />;
    }
});

const DownloadButton = React.memo((props: { language: string; text: string; noBorder?: boolean }) => {
    return (
        <div className="relative inline-flex items-center justify-center w-[140px] h-[35px] xl:w-[169px] xl:h-[40px]">
            <StaticImage
                src="../images/btn-box-s@3x.png"
                alt=""
                className="!absolute !inset-0 !hidden xl:!block"
                width={169}
                height={40}
            />
            <StaticImage
                src="../images/btn-box-s@3x.png"
                alt=""
                className="!absolute !inset-0 xl:!hidden"
                width={140}
                height={35}
            />
            <span className="relative flex items-center gap-1 text-white text-sm xl:text-base tracking-wide xl:tracking-widest whitespace-nowrap">
                {props.noBorder ? (
                    <StaticImage
                        src="../images/btn-download-m-2x-no-border.png"
                        alt=""
                        width={28}
                        height={28}
                    />
                ) : (
                    <StaticImage
                        src="../images/btn-download-m-2x.png"
                        alt=""
                        width={22}
                        height={22}
                    />
                )}
                {props.text}
            </span>
        </div>
    );
});

const SocialButton = React.memo((props: { text: string }) => {
    return (
        <div className="relative inline-flex items-center justify-center w-[140px] h-[35px] xl:w-[169px] xl:h-[40px]">
            <StaticImage
                src="../images/btn-box-s@3x.png"
                alt=""
                className="!absolute !inset-0 !hidden xl:!block"
                width={169}
                height={40}
            />
            <StaticImage
                src="../images/btn-box-s@3x.png"
                alt=""
                className="!absolute !inset-0 xl:!hidden"
                width={140}
                height={35}
            />
            <span className="relative flex items-center gap-1.5 text-white text-base tracking-widest">
                <StaticImage
                    src="../images/share.png"
                    alt=""
                    width={22}
                    height={22}
                />
                {props.text}
            </span>
        </div>
    );
});

const SOCIAL_PLATFORM_CONFIG: Record<SocialLink['platform'], { label: string }> = {
    facebook: { label: "Facebook" },
    youtube: { label: "YouTube" },
    discord: { label: "Discord" },
    x: { label: "X" },
    bilibili: { label: "Bilibili" },
    community: { label: "Community" },
};

const SocialIcon: React.FC<{ platform: SocialLink['platform'] }> = ({ platform }) => {
    const iconClass = "!w-6 !h-6 shrink-0";
    switch (platform) {
        case "youtube":
            return <StaticImage src="../images/ico-snk-ytb.png" alt="YouTube" width={24} height={24} className={iconClass} />;
        case "facebook":
            return <StaticImage src="../images/ico-snk-fb.png" alt="Facebook" width={24} height={24} className={iconClass} />;
        case "discord":
            return <StaticImage src="../images/ico-snk-dsr.png" alt="Discord" width={24} height={24} className={iconClass} />;
        case "x":
            return <StaticImage src="../images/ico-snk-x.png" alt="X" width={24} height={24} className={iconClass} />;
        case "bilibili":
            return <StaticImage src="../images/ico-snk-bili.png" alt="Bilibili" width={24} height={24} className={iconClass} />;
        case "community":
            return <StaticImage src="../images/ico-snk-fb.png" alt="Community" width={24} height={24} className={iconClass} />;
        default:
            return null;
    }
};


export const Header = () => {
    const { t, language } = useI18next();
    const isMobile = useMediaQuery("(max-width: 1280px)", true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const { isMobilePlatform, isIOS, isAndroid } = usePlatform()
    const { social_links, download_links } = useMetadata();

    const [isTop, toggleBg] = useToggle(true);
    const { setIsOpen } = useDMMPopupState();

    const handleMenuClose = () => setMenuOpen(false);
    const handleLanguageClose = () => setLanguageOpen(false);

    useScroll(
        ({ xy: [, y] }: any) => {
            const shouldShowBg = y > 0;
            if (shouldShowBg !== !isTop) {
                toggleBg();
            }
        },
        { target: typeof window !== "undefined" ? window : undefined }
    );

    return (
        <>
            <header
                className={cx("fixed top-0 inset-x-0 z-30 transition", {
                    "bg-[#0c0b0b]/80": !isTop,
                })}
            >
                {isDMM && <DmmHeader />}
                <div className="flex p-4 xl:py-0 xl:h-[66px] xl:relative xl:items-center xl:px-5 ">
                    {!isDMM && (
                        <button
                            type="button"
                            className="xl:hidden"
                            onClick={() => setLanguageOpen(true)}
                        >
                            <StaticImage
                                src="../images/btn-lang-m-2x.png"
                                alt="language"
                                loading="eager"
                                width={44}
                            />
                        </button>
                    )}
                    <Link to="/" className="hidden xl:block">
                        <Logo language={language} />
                    </Link>
                    <div className="hidden mx-auto transform 2xl:absolute 2xl:-translate-x-1/2 2xl:-translate-y-1/2 left-1/2 top-1/2 xl:block">
                        <Nav onClick={handleMenuClose} />
                    </div>
                    {isDMM ? (
                        <button
                            type="button"
                            className="ml-auto"
                            onClick={() => {
                                if(isMobilePlatform){
                                    window.open("https://games.dmm.co.jp/detail/riseoferos")}
                                else {
                                    setIsOpen(true)
                                }
                            }}
                        >
                            <DownloadButton language="ja" text="今すぐDL" noBorder />
                        </button>
                    ) : (
                        <Menu
                            as="div"
                            className="ml-auto relative hidden xl:block"
                        >
                            <Menu.Button 
                                className="focus:outline-0 h-[35px] xl:h-[40px] flex items-center"
                                aria-label={t("button.download_now")}
                            >
                                <DownloadButton language={language} text={language === 'en' ? 'Download' : t("button.download_now")} noBorder />
                            </Menu.Button>
                            <Transition
                                enter="transition ease-out duration-100"
                                enterFrom="transform-opacity opacity-0"
                                enterTo="transform-opacity opacity-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform-opacity opacity-100"
                                leaveTo="transform-opacity opacity-0"
                            >
                                <Menu.Items className="absolute top-full left-0 mt-1.5 space-y-1.5 focus:outline-none" static>
                                    {[
                                        {
                                            text: "iOS",
                                            href: download_links?.ios ?? '#',
                                            icon: <StaticImage alt="iOS" src="../images/ico-ios.png" width={28} height={28} className="shrink-0" />,
                                        },
                                        {
                                            text: "Android",
                                            href: download_links?.android ?? '#',
                                            icon: <StaticImage alt="Android" src="../images/ico-android.png" width={28} height={28} className="shrink-0" />,
                                        },
                                        {
                                            text: "STEAM",
                                            href: download_links?.steam ?? '#',
                                            icon: <StaticImage alt="Steam" src="../images/ico-steam@3x.png" width={28} height={28} className="shrink-0" />,
                                        },
                                    ].map(({ text, href, icon }) => (
                                        <Menu.Item key={text}>
                                            {({ active }) => (
                                                <a
                                                    href={href}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                    className="grid w-[140px] h-8 xl:w-[169px] xl:h-[40px]"
                                                >
                                                    <StaticImage
                                                        src="../images/btn-box-s@3x.png"
                                                        alt=""
                                                        loading="eager"
                                                        className="col-span-full row-span-full"
                                                    />
                                                    <span className="col-span-full row-span-full flex items-center px-3 text-white text-sm tracking-widest z-0">
                                                        {icon}
                                                        <span className="flex-grow text-center">
                                                            {text}
                                                        </span>
                                                    </span>
                                                </a>
                                            )}
                                        </Menu.Item>
                                    ))}
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                    {!isDMM && (
                        <Menu
                            as="div"
                            className="hidden xl:inline-block xl:ml-1.5 relative"
                        >
                            <Menu.Button 
                                className="focus:outline-0 h-[35px] xl:h-[40px] flex items-center"
                                aria-label={t("follow_us")}
                            >
                                <SocialButton text={t("follow_us")} />
                            </Menu.Button>
                            <Transition
                                enter="transition ease-out duration-100"
                                enterFrom="transform-opacity opacity-0"
                                enterTo="transform-opacity opacity-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform-opacity opacity-100"
                                leaveTo="transform-opacity opacity-0"
                            >
                                <Menu.Items className="absolute mt-1.5 right-0 space-y-1.5 focus:outline-none">
                                    {social_links?.map((link) => (
                                        <Menu.Item key={link.platform}>
                                            {({ active }) => (
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Follow us on ${SOCIAL_PLATFORM_CONFIG[link.platform]?.label ?? link.platform}`}
                                                    className="grid w-[138px] h-8 xl:w-[169px] xl:h-[40px] hover:opacity-90 transition-opacity"
                                                >
                                                    <StaticImage
                                                        src="../images/btn-box-s@3x.png"
                                                        alt=""
                                                        loading="eager"
                                                        className="col-span-full row-span-full"
                                                    />
                                                    <span className="col-span-full row-span-full flex items-center text-white text-sm tracking-widest z-0 px-3 gap-2">
                                                        <SocialIcon platform={link.platform} />
                                                        <span className="flex-1 text-center">
                                                            {SOCIAL_PLATFORM_CONFIG[link.platform]?.label ?? link.platform}
                                                        </span>
                                                    </span>
                                                </a>
                                            )}
                                        </Menu.Item>
                                    ))}
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                    {/* 手機版：下載 & 社群按鈕 */}
                    {!isDMM && (
                        <div className="flex items-center gap-1.5 xl:hidden ml-auto">
                            <a
                                href={
                                    isIOS
                                        ? (download_links?.ios ?? '#')
                                        : (download_links?.android ?? '#')
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t("button.download_now")}
                            >
                                <StaticImage
                                    src="../images/btn-download-m-2x.png"
                                    alt=""
                                    width={44}
                                    height={44}
                                />
                            </a>
                            <Menu as="div" className="relative">
                                <Menu.Button
                                    className="w-[44px] h-[44px] flex items-center justify-center focus:outline-0"
                                    aria-label={t("follow_us")}
                                >
                                    <StaticImage
                                        src="../images/btn-share-m-2x.png"
                                        alt="follow us"
                                        loading="eager"
                                        width={44}
                                    />
                                </Menu.Button>
                                <Transition
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform-opacity opacity-0"
                                    enterTo="transform-opacity opacity-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform-opacity opacity-100"
                                    leaveTo="transform-opacity opacity-0"
                                >
                                    <Menu.Items className="absolute top-full right-0 mt-1.5 flex flex-col gap-1 focus:outline-none">
                                        {social_links?.map((link) => (
                                            <Menu.Item key={link.platform}>
                                                {({ active }) => (
                                                    <a
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Follow us on ${SOCIAL_PLATFORM_CONFIG[link.platform]?.label ?? link.platform}`}
                                                        className="grid w-[169px] h-[40px] hover:opacity-90 transition-opacity"
                                                    >
                                                        <StaticImage
                                                            src="../images/btn-box-s@3x.png"
                                                            alt=""
                                                            loading="eager"
                                                            className="col-span-full row-span-full"
                                                        />
                                                        <span className="col-span-full row-span-full flex items-center text-white text-base tracking-widest z-0 px-3 gap-2">
                                                            <SocialIcon platform={link.platform} />
                                                            <span className="flex-1 text-center">
                                                                {SOCIAL_PLATFORM_CONFIG[link.platform]?.label ?? link.platform}
                                                            </span>
                                                        </span>
                                                    </a>
                                                )}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    )}
                    <button
                        type="button"
                        className="ml-1.5 xl:hidden"
                        onClick={() => setMenuOpen(true)}
                    >
                        <StaticImage
                            src="../images/btn-menu-m-2x.png"
                            alt="menu"
                            loading="eager"
                            width={44}
                        />
                    </button>
                    {!isDMM && (
                        <Menu
                            as="div"
                            className="hidden xl:inline-flex xl:ml-1.5 items-center"
                        >
                            <div className="mt-1">
                                <Menu.Button 
                                    className="relative inline-flex items-center justify-center w-[35px] h-[35px] xl:w-[40px] xl:h-[40px]"
                                    aria-label="Select language"
                                >
                                    <StaticImage
                                        src="../images/btn-lang-m-2x.png"
                                        alt="language"
                                        loading="eager"
                                        width={40}
                                        height={40}
                                    />
                                </Menu.Button>
                            </div>
                            <Transition
                                enter="transition ease-out duration-100"
                                enterFrom="transform-opacity opacity-0"
                                enterTo="transform-opacity opacity-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform-opacity opacity-100"
                                leaveTo="transform-opacity opacity-0"
                            >
                                <Menu.Items className="absolute grid top-full mt-1.5 right-6 focus:outline-none">
                                    <StaticImage
                                        className="col-span-full row-span-full -z-10"
                                        src="../images/btn-lang-menu-bg.png"
                                        alt="background"
                                    />
                                    <div className="grid grid-rows-5 px-1 py-6 col-span-full row-span-full">
                                        {LANGUAGES.map((props) => (
                                            <Menu.Item key={props.display}>
                                                <div className="text-center">
                                                    <LanguageItem
                                                        onClick={
                                                            handleLanguageClose
                                                        }
                                                        {...props}
                                                    />
                                                </div>
                                            </Menu.Item>
                                        ))}
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                </div>
            </header>
            <Modal
                showClose
                open={menuOpen && !!isMobile}
                onClose={handleMenuClose}
            >
                <Nav onClick={handleMenuClose} />
            </Modal>
            <Modal
                showClose
                open={languageOpen && !!isMobile}
                onClose={handleLanguageClose}
            >
                <nav>
                    <ul className="flex flex-col items-center space-y-4">
                        {LANGUAGES.map((props) => (
                            <li key={props.display}>
                                <LanguageItem
                                    onClick={handleLanguageClose}
                                    {...props}
                                />
                            </li>
                        ))}
                    </ul>
                </nav>
            </Modal>
        </>
    );
};

const LanguageItem: React.FC<
    typeof LANGUAGES[number] & { onClick: () => void }
> = ({ display, language, onClick }) => {
    const { changeLanguage } = useI18next();
    const handleClick = async () => {
        await changeLanguage(language);
        onClick();
    };

    return (
        <button
            type="button"
            className="text-xl text-white"
            onClick={handleClick}
        >
            {display}
        </button>
    );
};

const Nav: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t, language } = useI18next();
    const data = useStaticQuery<Queries.NavQuery>(graphql`
        query Nav {
            characters: allMarkdownRemark(
                filter: {
                    frontmatter: { type: { eq: "character" } }
                    fields: { language: { eq: "en" } }
                }
                sort: { fields: frontmatter___sort_order, order: ASC }
                limit: 1
            ) {
                edges {
                    node {
                        frontmatter {
                            slug
                        }
                    }
                }
            }
        }
    `);
    const pages = [
        { display: t("nav.home"), link: "/" },
        { display: t("nav.news"), link: getNewsUrl(language), isExternal: getNewsUrl(language).startsWith('http') },
        {
            display: t("nav.characters"),
            link:
                "/characters/" +
                data.characters.edges[0].node.frontmatter?.slug,
        },
        { display: t("nav.boudoir"), link: "/boudoir" },
        isDMM
            ? { display: "お問合せ", link: "https://support.pinkcore.com", isExternal: true }
            : { display: t("nav.faq"), link: "/faq" },
    ];

    return (
        <nav>
            <ul className="flex flex-col items-center space-y-4 xl:flex-row xl:space-y-0">
                {pages.map(({ display, link, isExternal }, i) => (
                    <li key={link} className="text-white">
                        {i > 0 && (
                            <span className="hidden mx-6 xl:inline-block">
                                /
                            </span>
                        )}
                        {isExternal ? (
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xl xl:hover:text-primary"
                                onClick={onClick}
                            >
                                {display}
                            </a>
                        ) : (
                            <Link
                                to={link}
                                className="text-xl xl:hover:text-primary"
                                activeClassName="xl:text-primary"
                                onClick={onClick}
                            >
                                {display}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
};
